import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Collapse,
    Button,
    alpha,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * DataCard
 * A standardized, premium mobile card component for data-heavy dashboards.
 * 
 * Props:
 * @param {string} id - Unique identifier for the card
 * @param {object} header - { title: string, subtitle: string }
 * @param {object} middleSection - { label: string, value: string|number, color: string }
 * @param {object} status - { label: string, color: string }
 * @param {boolean} expandable - Whether the card can be expanded
 * @param {node} renderExpandedContent - Function or node to render inside the collapse
 * @param {array} actions - Array of { label, onClick, color, variant }
 * @param {boolean} initiallyExpanded - Initial state of the card
 * @param {string} layout - 'split' (2-column) or 'vertical' (stacked metrics below title)
 */
const DataCard = React.memo(({
    id,
    header = { title: '', subtitle: '' },
    middleSection = { label: '', value: '', color: '' },
    status = { label: '', color: '' },
    expandable = false,
    renderExpandedContent = null,
    actions = [],
    initiallyExpanded = false,
    layout = 'split'
}) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(initiallyExpanded);

    const handleToggleExpand = () => {
        if (expandable) {
            setExpanded(!expanded);
        }
    };

    return (
        <Card sx={{
            height: 'auto',
            backgroundColor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: expanded ? `0 8px 16px ${alpha(theme.palette.common.black, 0.08)}` : 'none',
            overflow: 'hidden',
            mb: 0.5,
            '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
            }
        }}>
            <CardContent
                onClick={handleToggleExpand}
                sx={{ p: '16px !important', cursor: expandable ? 'pointer' : 'default' }}
            >
                {layout === 'vertical' ? (
                    /* Vertical Layout: Metrics below title, left-aligned */
                    <Box sx={{ position: 'relative' }}>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                fontWeight: 900, 
                                fontSize: '1.05rem', 
                                color: theme.palette.text.primary,
                                lineHeight: 1.1,
                                mb: 0.3,
                                pr: expandable ? 4 : 0 // Space for expand icon
                            }}
                        >
                            {header.title}
                        </Typography>
                        {header.subtitle && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.secondary', 
                                    fontSize: '0.75rem', 
                                    display: 'block',
                                    lineHeight: 1.1,
                                    mb: 1,
                                    opacity: 0.8
                                }}
                            >
                                {header.subtitle}
                            </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontWeight: 900, 
                                    fontSize: '0.95rem', 
                                    color: middleSection.color || theme.palette.text.primary,
                                    lineHeight: 1
                                }}
                            >
                                {middleSection.value}
                            </Typography>
                            {status.label && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: status.color || theme.palette.primary.main,
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        lineHeight: 1
                                    }}
                                >
                                    {status.label}
                                </Typography>
                            )}
                        </Box>

                        {expandable && (
                            <ExpandMoreIcon sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                fontSize: '1.2rem',
                                color: alpha(theme.palette.text.secondary, 0.4),
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s'
                            }} />
                        )}
                    </Box>
                ) : (
                    /* Split Layout: Default 2-column stacked view */
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={7.5}>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontWeight: 900, 
                                    fontSize: '1rem', 
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.1,
                                    mb: 0.2
                                }}
                            >
                                {header.title}
                            </Typography>
                            {header.subtitle && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'text.secondary', 
                                        fontSize: '0.75rem', 
                                        display: 'block',
                                        lineHeight: 1.1,
                                        opacity: 0.8
                                    }}
                                >
                                    {header.subtitle}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={4.5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            fontWeight: 900, 
                                            fontSize: '0.95rem', 
                                            color: middleSection.color || theme.palette.text.primary,
                                            lineHeight: 1
                                        }}
                                    >
                                        {middleSection.value}
                                    </Typography>
                                    {status.label && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                display: 'block',
                                                color: status.color || theme.palette.primary.main,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                mt: 0.5,
                                                lineHeight: 1
                                            }}
                                        >
                                            {status.label}
                                        </Typography>
                                    )}
                                </Box>
                                
                                {expandable && (
                                    <ExpandMoreIcon sx={{
                                        fontSize: '1.2rem',
                                        color: alpha(theme.palette.text.secondary, 0.4),
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s',
                                        ml: 0.5
                                    }} />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {/* Collapsible Content */}
                {expandable && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}` }}>
                            {/* Render custom details (Progress bars, etc.) */}
                            {renderExpandedContent && renderExpandedContent}

                            {/* Standardized Action Bar */}
                            {actions.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                                    {actions.map((action, idx) => (
                                        <Button
                                            key={idx}
                                            size="small"
                                            variant={action.variant || "contained"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                action.onClick(e);
                                            }}
                                            sx={{
                                                backgroundColor: action.color || 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: action.color ? alpha(action.color, 0.9) : 'primary.dark',
                                                },
                                                minWidth: '60px',
                                                height: '28px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                borderRadius: '14px',
                                                textTransform: 'none'
                                            }}
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                )}
            </CardContent>
        </Card>
    );
});

export default DataCard;
